import React, { useState, useEffect, useRef } from 'react';
import './MobileAddPersonModal.css';

function MobileAddPersonModal({
  isOpen,
  onClose,
  groups,
  onSubmit,
  onResearch,
  onCreateGroup,
  prefillName,
  prefillNote,
  prefillGroupId
}) {
  const [step, setStep] = useState('input');
  const [name, setName] = useState('');
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [note, setNote] = useState('');
  const [additionalContext, setAdditionalContext] = useState('');
  const [researchResults, setResearchResults] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedMatchIndex, setSelectedMatchIndex] = useState(null);

  // Inline group creation state
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupContext, setNewGroupContext] = useState('');
  const [isCreatingGroup, setIsCreatingGroup] = useState(false);
  const [createdGroupInModal, setCreatedGroupInModal] = useState(null); // Track if group was just created

  const nameInputRef = useRef(null);
  const noteInputRef = useRef(null);
  const newGroupInputRef = useRef(null);

  // Track if modal was just opened (to avoid resetting on groups change)
  const wasOpenRef = useRef(false);

  // Reset when modal opens (but not when groups change while open)
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      // Modal just opened
      wasOpenRef.current = true;
      setStep('input');
      setName(prefillName || '');
      setSelectedGroupId(prefillGroupId || null);
      setNote(prefillNote || '');
      setAdditionalContext('');
      setResearchResults([]);
      setIsSubmitting(false);
      setSelectedMatchIndex(null);
      setShowCreateGroup(false);
      setNewGroupName('');
      setNewGroupContext('');
      setIsCreatingGroup(false);
      setCreatedGroupInModal(null);

      // If no groups exist, show create group form automatically
      if (!groups || groups.length === 0) {
        setShowCreateGroup(true);
        setTimeout(() => newGroupInputRef.current?.focus(), 100);
      } else {
        setTimeout(() => {
          if (prefillName) {
            noteInputRef.current?.focus();
          } else {
            nameInputRef.current?.focus();
          }
        }, 100);
      }
    } else if (!isOpen) {
      // Modal closed
      wasOpenRef.current = false;
    }
  }, [isOpen, prefillName, prefillNote, groups]);

  // Handle inline group creation
  const handleCreateGroup = async () => {
    if (!newGroupName.trim() || isCreatingGroup || !onCreateGroup) return;

    setIsCreatingGroup(true);
    try {
      const newGroup = await onCreateGroup(newGroupName.trim(), newGroupContext.trim());
      if (newGroup?.id) {
        setSelectedGroupId(newGroup.id);
        setCreatedGroupInModal(newGroup); // Track that this group was just created
        setShowCreateGroup(false);
        setNewGroupName('');
        setNewGroupContext('');
        // Focus name input after creating group
        setTimeout(() => nameInputRef.current?.focus(), 100);
      }
    } catch (error) {
      console.error('Failed to create group:', error);
    } finally {
      setIsCreatingGroup(false);
    }
  };

  const handleNext = async () => {
    if (!name.trim() || !selectedGroupId) return;
    setStep('researching');

    try {
      const selectedGroup = groups?.find(g => g.id === selectedGroupId);
      const groupContext = selectedGroup
        ? `Group: ${selectedGroup.name}${selectedGroup.context ? ` - ${selectedGroup.context}` : ''}`
        : '';
      const fullContext = [groupContext, note, additionalContext].filter(Boolean).join('. ');

      let result = await onResearch(name.trim(), fullContext);

      // If result is a JSON string, try to parse it
      if (typeof result === 'string' && result.trim().startsWith('{')) {
        try {
          result = JSON.parse(result);
        } catch (e) {
          // Not valid JSON, keep as string
        }
      }

      if (result) {
        if (typeof result === 'object' && result.matches && result.matches.length > 0) {
          setResearchResults(result.matches);
          setStep('results');
        } else if (typeof result === 'object' && result.notFound) {
          setStep('notFound');
        } else if (typeof result === 'string' && result.trim()) {
          // Only use as raw text if it doesn't look like JSON
          const text = result.trim();
          if (!text.startsWith('{') && !text.startsWith('[')) {
            setResearchResults([{ name: name.trim(), info: text, summary: text.slice(0, 150) }]);
            setStep('results');
          } else {
            setStep('notFound');
          }
        } else if (typeof result === 'object' && result.info) {
          setResearchResults([{ name: name.trim(), info: result.info, summary: result.info.slice(0, 150) }]);
          setStep('results');
        } else {
          setStep('notFound');
        }
      } else {
        setStep('notFound');
      }
    } catch (error) {
      console.error('Research error:', error);
      setStep('notFound');
    }
  };

  const handleRetryResearch = async () => {
    if (!additionalContext.trim()) {
      await handleAddPerson('');
      return;
    }
    setStep('researching');

    try {
      let result = await onResearch(name.trim(), additionalContext.trim());

      // If result is a JSON string, try to parse it
      if (typeof result === 'string' && result.trim().startsWith('{')) {
        try {
          result = JSON.parse(result);
        } catch (e) {
          // Not valid JSON, keep as string
        }
      }

      if (result) {
        if (typeof result === 'object' && result.matches && result.matches.length > 0) {
          setResearchResults(result.matches);
          setStep('results');
        } else if (typeof result === 'string' && result.trim() && !result.trim().startsWith('{')) {
          setResearchResults([{ name: name.trim(), info: result, summary: result.slice(0, 150) }]);
          setStep('results');
        } else {
          await handleAddPerson(additionalContext.trim());
        }
      } else {
        await handleAddPerson(additionalContext.trim());
      }
    } catch (error) {
      await handleAddPerson(additionalContext.trim());
    }
  };

  const handleAddPerson = async (info) => {
    setIsSubmitting(true);
    try {
      await onSubmit(name.trim(), info || '', selectedGroupId, note || '');
      onClose();
    } catch (error) {
      console.error('Add person error:', error);
      setIsSubmitting(false);
    }
  };

  const handleSelectMatch = async (match, index) => {
    setSelectedMatchIndex(index);
    setIsSubmitting(true);
    await handleAddPerson(match.info);
  };

  if (!isOpen) return null;

  const selectedGroup = groups?.find(g => g.id === selectedGroupId);
  const hasGroups = groups && groups.length > 0;
  const canProceed = name.trim() && selectedGroupId;

  return (
    <div className="mobile-add-person-overlay" onClick={onClose}>
      <div className="mobile-add-person-sheet" onClick={e => e.stopPropagation()}>
        <div className="mobile-add-person-handle" />

        <div className="mobile-add-person-header">
          <h2>Add Person</h2>
          <span className="mobile-add-person-subtitle">
            {step === 'input' && (showCreateGroup ? 'Create a group first' : 'Enter details')}
            {step === 'researching' && `Researching ${name}...`}
            {step === 'results' && 'Select match'}
            {step === 'notFound' && 'Add context'}
          </span>
        </div>

        <div className="mobile-add-person-content">
          {step === 'input' && (
            <>
              {/* Show create group form if no groups or user clicked create */}
              {showCreateGroup ? (
                <div className="mobile-add-person-create-group">
                  <div className="mobile-add-person-create-group-header">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                      <circle cx="9" cy="7" r="4" />
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                    </svg>
                    <span>{hasGroups ? 'Create new group' : 'Create your first group'}</span>
                  </div>
                  <input
                    ref={newGroupInputRef}
                    type="text"
                    className="mobile-add-person-input"
                    placeholder="Group name (e.g., Board Members)"
                    value={newGroupName}
                    onChange={(e) => setNewGroupName(e.target.value)}
                  />
                  <input
                    type="text"
                    className="mobile-add-person-input"
                    placeholder="Context (optional)"
                    value={newGroupContext}
                    onChange={(e) => setNewGroupContext(e.target.value)}
                  />
                  <div className="mobile-add-person-create-group-actions">
                    {hasGroups && (
                      <button
                        className="mobile-add-person-cancel"
                        onClick={() => setShowCreateGroup(false)}
                      >
                        Cancel
                      </button>
                    )}
                    <button
                      className="mobile-add-person-next"
                      onClick={handleCreateGroup}
                      disabled={!newGroupName.trim() || isCreatingGroup}
                    >
                      {isCreatingGroup ? 'Creating...' : 'Create Group'}
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <input
                    ref={nameInputRef}
                    type="text"
                    className="mobile-add-person-input"
                    placeholder="Person's name..."
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                  <textarea
                    ref={noteInputRef}
                    className="mobile-add-person-note"
                    placeholder="Quick note... how you met, why they matter"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    rows={2}
                  />
                  {/* Group Selection - Required (hidden if group was just created) */}
                  {createdGroupInModal ? (
                    <div className="mobile-add-person-created-group">
                      <span className="group-dot" style={{ background: createdGroupInModal.color || '#a78bfa' }} />
                      <span>Adding to <strong>{createdGroupInModal.name}</strong></span>
                    </div>
                  ) : (
                    <div className="mobile-add-person-groups">
                      <div className="mobile-add-person-groups-label">
                        Add to group <span className="mobile-required">*</span>
                      </div>
                      <div className="mobile-add-person-groups-list">
                        {groups?.map((group) => (
                          <button
                            key={group.id}
                            className={`mobile-add-person-group-btn ${selectedGroupId === group.id ? 'selected' : ''}`}
                            onClick={() => setSelectedGroupId(group.id)}
                          >
                            <span className="group-dot" style={{ background: group.color || '#a78bfa' }} />
                            {group.name}
                          </button>
                        ))}
                        <button
                          className="mobile-add-person-group-btn create-new"
                          onClick={() => {
                            setShowCreateGroup(true);
                            setTimeout(() => newGroupInputRef.current?.focus(), 100);
                          }}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <path d="M12 5v14M5 12h14" />
                          </svg>
                          New Group
                        </button>
                      </div>
                      {!selectedGroupId && name.trim() && (
                        <div className="mobile-add-person-group-hint">
                          Select a group to continue
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {step === 'researching' && (
            <div className="mobile-add-person-loading">
              <div className="mobile-add-person-spinner" />
              <span>Researching {name}...</span>
            </div>
          )}

          {step === 'results' && (
            <div className="mobile-add-person-results">
              {researchResults.map((match, i) => {
                const isSelected = selectedMatchIndex === i;
                // Never show JSON to user - strip all brackets and JSON artifacts
                const sanitize = (val) => {
                  if (!val) return '';
                  return String(val)
                    .replace(/[{}\[\]"]/g, '')  // Remove all brackets, braces, quotes
                    .replace(/\\n/g, ' ')        // Replace escaped newlines
                    .replace(/\\"/g, '')         // Remove escaped quotes
                    .replace(/,\s*:/g, ': ')     // Clean up JSON artifacts
                    .replace(/:\s*,/g, ' ')      // Clean up empty values
                    .replace(/\b(matches|name|summary|info|notFound)\b\s*:/gi, '') // Remove JSON keys
                    .replace(/\s+/g, ' ')        // Normalize whitespace
                    .trim();
                };
                const displayName = sanitize(match.name) || name;
                const displaySummary = sanitize(match.summary || match.info || '').substring(0, 150);

                return (
                  <button
                    key={i}
                    className={`mobile-add-person-match ${isSelected ? 'adding' : ''}`}
                    onClick={() => handleSelectMatch(match, i)}
                    disabled={isSubmitting}
                  >
                    {isSelected ? (
                      <div className="mobile-add-person-loading">
                        <div className="mobile-add-person-spinner" />
                        <span>Adding...</span>
                      </div>
                    ) : (
                      <>
                        <div className="match-avatar">
                          {displayName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                        </div>
                        <div className="match-info">
                          <div className="match-name">{displayName}</div>
                          <div className="match-summary">{displaySummary}</div>
                        </div>
                      </>
                    )}
                  </button>
                );
              })}
              <button
                className="mobile-add-person-none-btn"
                onClick={() => setStep('notFound')}
                disabled={isSubmitting}
              >
                None of these match
              </button>
            </div>
          )}

          {step === 'notFound' && (
            <div className="mobile-add-person-not-found">
              <p>Couldn't find <strong>{name}</strong></p>
              <textarea
                className="mobile-add-person-context"
                placeholder="Add more context or describe who they are..."
                value={additionalContext}
                onChange={(e) => setAdditionalContext(e.target.value)}
                disabled={isSubmitting}
              />
              {selectedGroup && (
                <div className="mobile-add-person-selected-group">
                  <span className="group-dot" style={{ background: selectedGroup.color || '#a78bfa' }} />
                  Adding to {selectedGroup.name}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="mobile-add-person-footer">
          {step === 'input' && !showCreateGroup && (
            <>
              <button className="mobile-add-person-cancel" onClick={onClose}>
                Cancel
              </button>
              <button
                className="mobile-add-person-next"
                onClick={handleNext}
                disabled={!canProceed}
              >
                Next
              </button>
            </>
          )}
          {step === 'results' && (
            <button className="mobile-add-person-cancel" onClick={onClose} style={{ flex: 1 }}>
              Cancel
            </button>
          )}
          {step === 'notFound' && (
            <>
              <button
                className="mobile-add-person-skip"
                onClick={() => handleAddPerson(additionalContext.trim() || '')}
                disabled={isSubmitting}
              >
                Skip Research
              </button>
              <button
                className="mobile-add-person-next"
                onClick={handleRetryResearch}
                disabled={isSubmitting || !additionalContext.trim()}
              >
                {isSubmitting ? 'Searching...' : 'Try Again'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default MobileAddPersonModal;
